/**
 * SaaS platform super-admin API (ADR-0021). Real /api/v1/platform/tenants.
 *
 * Only the platform super-admin (a user in the reserved PLATFORM tenant carrying
 * the `platform_superadmin` role) can call these — the backend enforces it with a
 * permission check AND a platform-realm assertion. The FE uses `fetchMe()` purely
 * as a post-login routing hint (clinic dashboard vs. platform console).
 */
import { apiClient } from "./client";
import type { ResultsLookup } from "./website";

export const PLATFORM_SUPERADMIN_ROLE = "platform_superadmin";

export interface PlatformTenant {
  id: string;
  code: string;
  name: string;
  status: string; // ACTIVE | SUSPENDED | OFFBOARDING | OFFBOARDED
  createdAt: string;
}

/** Một màn hình của một phòng khám: gói cho gì, và có chỉnh riêng không (ADR-0053). */
export interface TenantScreen {
  code: string;
  inPlan: boolean;
  /**
   * Giá trị chỉnh riêng. VẮNG (hoặc `null`) = theo gói.
   *
   * Khai `?` vì máy chủ bỏ hẳn trường rỗng khỏi JSON: màn hình theo gói tới đây KHÔNG có khoá
   * `override`, tức `undefined` chứ không phải `null`. So sánh `!== null` đã từng vẽ mọi màn hình
   * thành "Chỉnh riêng" — dùng `!= null`.
   */
  override?: boolean | null;
  reason?: string | null;
  /** Phòng khám có thấy màn hình này không, sau khi tính cả gói lẫn chỉnh riêng. */
  enabled: boolean;
}

export interface TenantScreens {
  /** Vắng (hoặc `null`) = phòng khám chưa có dòng thuê bao nào. */
  planCode?: string | null;
  /**
   * Chặn theo gói đã bật cho phòng khám này chưa. Chưa bật thì `enabled` của từng màn hình mới
   * chỉ là điều SẼ xảy ra: phòng khám vẫn thấy toàn bộ menu.
   */
  enforced: boolean;
  screens: TenantScreen[];
}

/** Gán gói không qua mua bán (ADR-0053). `activeTo` rỗng = gói không hết hạn. */
export interface AssignPlanInput {
  planCode: string;
  activeTo: string | null;
  reason: string;
}

export interface CreateTenantInput {
  clinicName: string;
  code: string;
  timezone: string;
  ownerEmail: string;
  ownerPassword: string;
}

export interface Me {
  userId: string;
  username: string;
  roleCodes: string[];
  /** False while a self-registered owner hasn't clicked the activation e-mail. */
  emailVerified?: boolean;
}

/** React Query keys for the current-user (`/me`) read. */
export const meQueryKeys = {
  self: () => ["me", "self"] as const,
};

export const platformApi = {
  me(): Promise<Me> {
    return apiClient.get<Me>("/v1/me").then((r) => r.data);
  },
  listTenants(): Promise<PlatformTenant[]> {
    return apiClient
      .get<PlatformTenant[]>("/v1/platform/tenants")
      .then((r) => r.data);
  },
  createTenant(body: CreateTenantInput): Promise<PlatformTenant> {
    return apiClient
      .post<PlatformTenant>("/v1/platform/tenants", body)
      .then((r) => r.data);
  },
  suspendTenant(id: string): Promise<PlatformTenant> {
    return apiClient
      .post<PlatformTenant>(`/v1/platform/tenants/${id}/suspend`)
      .then((r) => r.data);
  },
  activateTenant(id: string): Promise<PlatformTenant> {
    return apiClient
      .post<PlatformTenant>(`/v1/platform/tenants/${id}/activate`)
      .then((r) => r.data);
  },
  /** #19191 — ô "tra cứu kết quả trên website" của một phòng khám, xem hộ. */
  resultsLookup(tenantId: string): Promise<ResultsLookup> {
    return apiClient
      .get<ResultsLookup>(`/v1/platform/tenants/${tenantId}/website-results-lookup`)
      .then((r) => r.data);
  },
  /** #19191 — tích/bỏ tích hộ phòng khám. Lý do bắt buộc: nó vào nhật ký của phòng khám. */
  setResultsLookup(
    tenantId: string,
    enabled: boolean,
    reason: string,
  ): Promise<ResultsLookup> {
    return apiClient
      .put<ResultsLookup>(`/v1/platform/tenants/${tenantId}/website-results-lookup`, {
        enabled,
        reason,
      })
      .then((r) => r.data);
  },
  tenantScreens(tenantId: string): Promise<TenantScreens> {
    return apiClient
      .get<TenantScreens>(`/v1/platform/tenants/${tenantId}/screens`)
      .then((r) => r.data);
  },
  /** Bật/tắt một màn hình cho RIÊNG phòng khám này. Lý do vào nhật ký của phòng khám. */
  overrideTenantScreen(
    tenantId: string,
    screenCode: string,
    enabled: boolean,
    reason: string,
  ): Promise<TenantScreens> {
    return apiClient
      .put<TenantScreens>(`/v1/platform/tenants/${tenantId}/screens/${screenCode}`, {
        enabled,
        reason,
      })
      .then((r) => r.data);
  },
  /** Gán gói cho phòng khám — không qua ví. Lý do vào nhật ký của phòng khám. */
  assignTenantPlan(tenantId: string, input: AssignPlanInput): Promise<void> {
    return apiClient.put(`/v1/platform/tenants/${tenantId}/plan`, input).then(() => undefined);
  },
  /** Bật/tắt chặn theo gói cho phòng khám này. 409 khi bật cho phòng khám chưa có gói. */
  setPlanEnforcement(tenantId: string, enabled: boolean): Promise<void> {
    return apiClient
      .put(`/v1/platform/tenants/${tenantId}/plan/enforcement`, { enabled })
      .then(() => undefined);
  },
  /** Bỏ chỉnh riêng: màn hình quay về theo gói. */
  clearTenantScreenOverride(tenantId: string, screenCode: string): Promise<TenantScreens> {
    return apiClient
      .delete<TenantScreens>(`/v1/platform/tenants/${tenantId}/screens/${screenCode}`)
      .then((r) => r.data);
  },
};

export const platformQueryKeys = {
  tenants: () => ["platform-tenants"] as const,
  tenantScreens: (tenantId: string) => ["platform-tenants", tenantId, "screens"] as const,
  resultsLookup: (tenantId: string) =>
    ["platform-tenants", tenantId, "results-lookup"] as const,
};

/** True when the given role codes include the platform super-admin role. */
export function isPlatformSuperadmin(roleCodes: string[] | undefined): boolean {
  return !!roleCodes?.includes(PLATFORM_SUPERADMIN_ROLE);
}

const PLATFORM_FLAG_KEY = "addy.isPlatformAdmin";

/** Remember (for this browser) whether the signed-in user is a platform admin. */
export function setPlatformAdminFlag(isAdmin: boolean): void {
  if (isAdmin) localStorage.setItem(PLATFORM_FLAG_KEY, "1");
  else localStorage.removeItem(PLATFORM_FLAG_KEY);
}

export function isPlatformAdminFlag(): boolean {
  return localStorage.getItem(PLATFORM_FLAG_KEY) === "1";
}
